import Form from '@rjsf/mui';
import type RjsfForm from '@rjsf/core';
import type { RJSFSchema, UiSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { Box, CircularProgress } from '@mui/material';
import { type ReactElement, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import MarketingStationLayout from 'app/shared-components/MarketingStationLayout';
import useHeaderActions from 'app/hooks/useHeaderActions';
import useHeaderTitle from 'app/hooks/useHeaderTitle';
import useNavigationHeader from 'app/hooks/useNavigationHeader';
import { CONTENT_TABS, websiteDetailsPath } from 'app/pages/PlusWebsites/paths';
import { UnsavedChangesDialog } from 'app/pages/PlusWebsites/components/UnsavedChangesDialog';
import { useGuardedSave } from 'app/pages/PlusWebsites/hooks/useGuardedSave';

import { fields } from '../../components/fields/fields';
import { templates } from '../../components/templates/templates';
import { widgets } from '../../components/widgets/widgets';
import { createMenuBuilderValidate } from '../../components/widgets/MenuBuilder/menuBuilderCustomValidate';

interface WebsiteSectionEditorProps {
  title: string;
  schema: RJSFSchema;
  uiSchema: UiSchema;
  onSubmit: (formData: Record<string, unknown>) => Promise<unknown>;
  formData?: Record<string, unknown>;
  isLoading?: boolean;
  isSaving?: boolean;
  /**
   * A section that does not exist yet: its schema defaults are already savable content, so Save
   * has to be live before the user edits anything.
   */
  isNew?: boolean;
}

export function WebsiteSectionEditor({
  title,
  schema,
  uiSchema,
  onSubmit,
  formData,
  isLoading = false,
  isSaving = false,
  isNew = false,
}: WebsiteSectionEditorProps): ReactElement {
  const formRef = useRef<RjsfForm | null>(null);
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const backPath = websiteDetailsPath(searchParams, CONTENT_TABS.headerAndFooter);

  const guard = useGuardedSave({
    formRef,
    onSubmit,
    backPath,
    isSaving,
    allowPristineSave: isNew,
  });

  // Creating a section pushes a fresh website-details entry, so history holds every add
  // and edit — back has to go up a level instead of replaying that trail.
  useNavigationHeader({
    title: 'Back',
    navigateTo: backPath,
    backToHistory: false,
    replace: true,
    onBeforeNavigate: guard.confirmNavigation,
  });
  useHeaderTitle(title);
  useHeaderActions([
    {
      title: isSaving ? 'Saving…' : 'Save changes',
      type: 'button',
      callback: () => formRef.current?.submit(),
      disabled: !guard.canSave || isSaving || isLoading,
    },
  ]);

  return (
    <MarketingStationLayout className="vtwo:flex vtwo:flex-col vtwo:h-full vtwo:overflow-hidden">
      <Box className="vtwo:flex-1 vtwo:bg-plus-muted vtwo:overflow-y-auto">
        <Box className="vtwo:max-w-2xl vtwo:mx-auto vtwo:py-16 vtwo:px-8">
          {isLoading && (
            <Box className="vtwo:flex vtwo:justify-center vtwo:py-24">
              <CircularProgress size={24} />
            </Box>
          )}

          {!isLoading && (
            <Box className="vtwo:bg-white vtwo:rounded-xl vtwo:border vtwo:border-plus-border vtwo:overflow-hidden vtwo:p-8">
              <Form
                ref={formRef}
                schema={schema}
                uiSchema={uiSchema}
                initialFormData={formData}
                templates={templates}
                widgets={widgets}
                fields={fields}
                validator={validator}
                customValidate={createMenuBuilderValidate(uiSchema)}
                // rjsf reports an id only for user edits, so loading defaults does not count.
                onChange={(_data, id) => {
                  if (id !== undefined) guard.markDirty();
                }}
                onSubmit={() => guard.save()}
                onError={() =>
                  enqueueSnackbar('Please fix the errors before saving', { variant: 'error' })
                }
                showErrorList={false}
                transformErrors={(errors) => {
                  return errors.filter((e) => e.name !== 'additionalProperties');
                }}
              >
                <button type="submit" hidden aria-label="Save" />
              </Form>
            </Box>
          )}
        </Box>
      </Box>

      <UnsavedChangesDialog {...guard.dialogProps} />
    </MarketingStationLayout>
  );
}
